import { useCallback, useRef, useState } from 'react';
import { ordersApi } from '../../api';
import type { SaleInput, SaleResult } from '../../types';

/**
 * Sotuvni yuborish.
 *
 * Idempotentlik kaliti birinchi urinishda yaratiladi va MUVAFFAQIYATGACHA
 * saqlanadi: mobil tarmoqda javob yo'qolib, foydalanuvchi qayta bosса ham
 * server bitta buyurtma yaratadi. Yangi sotuv boshlanganda `reset()` kalitni
 * tozalaydi.
 */
export function usePosCheckout() {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const idempotencyKey = useRef<string>('');

  const reset = useCallback(() => {
    idempotencyKey.current = '';
    setError(null);
  }, []);

  const submit = useCallback(async (input: Omit<SaleInput, 'idempotency_key'>): Promise<SaleResult | null> => {
    if (!idempotencyKey.current) {
      idempotencyKey.current =
        globalThis.crypto?.randomUUID?.() ??
        `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    }

    setSubmitting(true);
    setError(null);
    try {
      const response = await ordersApi.create({
        ...input,
        idempotency_key: idempotencyKey.current,
      });
      return response.data;
    } catch (err: any) {
      setError(readError(err));
      return null;
    } finally {
      setSubmitting(false);
    }
  }, []);

  return { submit, submitting, error, setError, reset };
}

/**
 * Serverning xato javobini o'qiladigan matnga aylantiradi.
 *
 * DRF xatolari uch xil shaklda keladi: `{error: "..."}`, `{detail: "..."}`
 * va maydon bo'yicha `{items: ["..."]}`. Oxirgisi eng foydalisi — omborda
 * yetmagan tovar nomi aynan shu yerda bo'ladi.
 */
function readError(err: any): string {
  const data = err?.response?.data;
  if (!data) return 'Tarmoq xatosi — qayta urinib ko\'ring.';
  if (typeof data === 'string') return data;
  if (data.error) return data.error;
  if (data.detail) return data.detail;

  const firstField = Object.keys(data).find((key) => {
    const value = data[key];
    return Array.isArray(value) ? value.length > 0 : typeof value === 'string';
  });
  if (firstField) {
    const value = data[firstField];
    return Array.isArray(value) ? String(value[0]) : String(value);
  }
  return 'Sotuvni saqlab bo\'lmadi.';
}
